// ---------------------------------------------------------------------------
// FC4 §B.4 — dicionário único `code → mensagem PT-BR`.
//
// Resolvido por `notifyApiError`; quando não há entrada, cai no
// `apiError.message` (que já vem apresentável do backend). Cresce sob demanda —
// não duplicar mensagem por página.
//
// Discrimine SEMPRE por `code`, nunca por texto da mensagem (é PT-BR e muda).
//
// Escopo atual: códigos genéricos + auth (handoffs 0097/0098). Os dicionários
// por módulo que já existem — `pages/Finance/utils/apiError.js` e os equivalentes
// de Vendas e Recrutamento — migram para cá ao serem tocados (FC1 §A.10), não em
// sweep.
// ---------------------------------------------------------------------------

// eslint-disable-next-line import/prefer-default-export
export const apiErrorMessages = {
   // ─── Genéricos ───────────────────────────────────────────────────────────
   network_error: 'Falha de conexão. Verifique sua internet e tente novamente.',
   unauthorized: 'Sessão inválida. Faça login novamente.',
   forbidden: 'Você não tem permissão para esta ação.',
   not_found: 'Registro não encontrado.',
   conflict: 'Esta operação conflita com o estado atual do registro.',
   unique: 'Já existe um registro com este valor.',
   rate_limit:
      'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.',
   payload_too_large:
      'O envio excedeu o tamanho aceito pelo servidor. Envie arquivos menores, ou um de cada vez.',

   // ─── Sessão / token (handoff 0097 §2.1) ──────────────────────────────────
   // `session_invalid` = versão do token divergente → sessão revogada de
   // verdade (troca de senha, reset). O usuário é levado ao login pelo saga;
   // esta mensagem é a que ele lê no caminho.
   session_invalid: 'Sua sessão foi encerrada. Faça login novamente.',
   // `token_outdated` = token legado sem o claim `token_version` (Etapa B).
   // A sessão está VIVA e o interceptor renova de forma transparente — se esta
   // mensagem aparecer para o usuário, é sinal de que algo saiu do trilho.
   token_outdated: 'Renovando sua sessão. Tente novamente.',

   // ─── Troca da própria senha (handoff 0097 §3) ────────────────────────────
   // Vem com `field: current_password` → normalmente renderiza inline, não em
   // toast. A entrada existe para o caso de consumo fora de formulário.
   invalid_current_password: 'A senha atual não confere.',

   // ─── Esqueci minha senha (handoff 0098 §3) ───────────────────────────────
   // Mensagem única e genérica de propósito: distinguir "expirado" de "errado"
   // entrega informação a quem está adivinhando o código.
   invalid_or_expired_code: 'Código inválido ou expirado. Peça um novo código.',

   // ─── Transferência de estoque entre PDVs (handoff 0099) ──────────────────
   // Escopo de PDV: expedir/editar/cancelar exigem acesso à ORIGEM; conferir,
   // ao DESTINO; tratativa, a qualquer ponta.
   pdv_access_denied:
      'Você não tem acesso ao PDV envolvido nesta operação de transferência.',
   invalid_state_transition:
      'O estado atual da transferência não permite esta ação. Recarregue a tela para ver a situação mais recente.',
   // Guards da ordem.
   transfer_requested_below_shipped:
      'A quantidade pedida não pode ficar abaixo do que já foi expedido.',
   transfer_order_has_shipments:
      'Esta transferência já tem remessa expedida e não pode mais ser cancelada. Cancele as remessas em trânsito primeiro.',
   // Guards da expedição.
   transfer_item_not_in_order:
      'Há item que não pertence a esta transferência. Recarregue a tela e refaça a expedição.',
   transfer_over_shipment:
      'A quantidade a expedir excede o saldo pendente do pedido.',
   // 404 do responsável pelo transporte (handoff 0108): o campo é opcional, mas
   // se vier preenchido tem de ser um funcionário existente.
   responsible_employee_not_found:
      'Responsável pelo transporte não encontrado — selecione um funcionário cadastrado.',
   // Guards da conferência.
   transfer_receive_missing_item:
      'A conferência precisa informar o contado de TODOS os itens da remessa.',
   transfer_receive_over_shipped:
      'A quantidade conferida não pode ser maior que a expedida — sobra não entra no estoque.',
   transfer_divergence_reason_required:
      'Informe o motivo da divergência nos itens em que o conferido é menor que o expedido.',
   // Trava do módulo Movements: o par out/in e os acumuladores da transferência
   // só se alteram pelo próprio fluxo de transferência.
   transfer_movement_locked:
      'Este movimento pertence a uma transferência entre PDVs e só pode ser alterado pelo fluxo de transferência.',

   // ─── Administração de usuários (handoff 0110) ────────────────────────────
   // `email_in_use` vem com `field: "email"` → a tela de criação renderiza
   // inline; a entrada existe para consumo fora de formulário.
   email_in_use:
      'Este e-mail já está em uso por uma conta ativa. Use outro endereço.',
   // O backend recusa o auto-delete; a ficha já esconde o botão na própria
   // conta, então este texto é a rede de segurança.
   self_delete_forbidden:
      'Você não pode excluir a sua própria conta. Peça a outro administrador.',
   role_not_found: 'Papel de acesso não encontrado. Recarregue a tela.',
   pdv_not_found: 'Há PDV inexistente na seleção. Recarregue a tela.',
   user_not_found: 'Usuário não encontrado.',
   missing_permission: 'Você não tem permissão para esta operação.',
};
